import type { TableRunStatus } from "@Simplicity-Education/core";
import { useEffect, useState } from "react";
import { apiFetch } from "../../api";
import type {
  TableRunStatusContextValue,
  TableRunStatusFetchStatus,
} from "./TableRunStatusContext";

/** Which connector's tables we want run facts for. */
export interface RunStatusTarget {
  projectId: string;
  connectorId: string;
}

interface RunStatusResponse {
  projectId: string;
  computedAt: string;
  lookbackDays: number;
  tables: TableRunStatus[];
}

interface State {
  key: string;
  status: TableRunStatusFetchStatus;
  byTable: Map<string, TableRunStatus>;
  computedAtMs: number;
  lookbackDays: number;
}

const EMPTY: Map<string, TableRunStatus> = new Map();

function targetKey(target: RunStatusTarget | null | undefined): string {
  return target == null ? "" : `${target.projectId}|${target.connectorId}`;
}

/**
 * SIM-2729: load per-table run facts from `GET /api/admin/run-status/:projectId`, replacing the
 * BQ-metadata source. Measured on prod, a silver attendance table regenerating hourly was reported
 * 21h stale, because `ClientMetadataReport` is 24h-gated — a table timestamp cannot answer "did
 * this run", a completed RunPlan step can. The endpoint carries no SQL.
 */
export function useTableRunStatus(
  target: RunStatusTarget | null | undefined,
): TableRunStatusContextValue {
  // Primitive deps, not the object: an inlined literal is a new reference every
  // render and would re-run the effect forever.
  const projectId = target?.projectId;
  const connectorId = target?.connectorId;
  const key = targetKey(target);
  const [state, setState] = useState<State>({
    key,
    status: "loading",
    byTable: EMPTY,
    computedAtMs: 0,
    lookbackDays: 0,
  });

  useEffect(() => {
    if (projectId == null || connectorId == null) return;
    const thisKey = `${projectId}|${connectorId}`;
    let cancelled = false;
    apiFetch<RunStatusResponse>(`/api/admin/run-status/${projectId}`)
      .then((res) => {
        if (cancelled) return;
        const byTable = new Map<string, TableRunStatus>();
        for (const row of res.tables) {
          if (row.connectorId !== connectorId) continue;
          byTable.set(row.table.trim().toLowerCase(), row);
        }
        const computedAtMs = Date.parse(res.computedAt);
        setState({
          key: thisKey,
          status: "ready",
          byTable,
          computedAtMs: Number.isFinite(computedAtMs) ? computedAtMs : Date.now(),
          lookbackDays: res.lookbackDays ?? 0,
        });
      })
      .catch(() => {
        // Supplementary data — must never break the config form it decorates.
        if (cancelled) return;
        setState({
          key: thisKey,
          status: "unavailable",
          byTable: EMPTY,
          computedAtMs: Date.now(),
          lookbackDays: 0,
        });
      });
    return () => {
      cancelled = true;
    };
  }, [projectId, connectorId]);

  // Derived, not stored: while the resolved state belongs to a previous target,
  // report loading rather than another connector's rows.
  if (state.key !== key) {
    return { byTable: EMPTY, status: "loading", computedAtMs: 0, lookbackDays: 0 };
  }
  return {
    byTable: state.byTable,
    status: state.status,
    computedAtMs: state.computedAtMs,
    lookbackDays: state.lookbackDays,
  };
}
